import React from 'react';

/**
 * One prefilled value on a scanned form template (I9Form, W4Form,
 * PayrollDirectDeposit, EmployeeRights). The templates are static images
 * with text hand-positioned on top by `top`/`left` pixel coordinates --
 * nothing on the page ever bounded how WIDE that text was allowed to get, so
 * a long name, address or state ran on in a straight line and drew over
 * whatever field came next (2026-09-28 report -- address overlapping the
 * apartment number, email running into the phone number, etc.).
 *
 * `d`/`p` each carry `{ top, left, width, fontSize? }` for the download
 * (794px, desktop preview) and mobile-preview (359px) layouts respectively
 * -- `width` is the real field's width at that scale, so text has somewhere
 * to go instead of running past it. A value that still doesn't fit at the
 * base font size shrinks first (down to a floor) and only wraps to a second
 * line if it's too long even at that size -- most values just get slightly
 * smaller and stay on one line, which reads closer to the real form than
 * text breaking across lines would. A field with no `width` (a checkmark, a
 * short fixed-format date) renders exactly as it always did.
 *
 * A field that also carries `height` (the W-4 does, taken from the form's own
 * field boxes) is fitted to the WHOLE box: the font shrinks, and the text wraps
 * onto more lines only if the box is tall enough for them, until it fits both
 * ways. The text is then centred vertically in the box. Widths are measured
 * with the real font rather than estimated.
 */

let measureContext;

const textWidth = (text, size) => {
  try {
    if (measureContext === undefined) {
      // jsdom (the test environment) has no canvas and logs a warning if asked for one.
      const inJsdom = typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent);
      measureContext = inJsdom ? null : document.createElement('canvas').getContext('2d') || null;
    }
    if (measureContext) {
      measureContext.font = `${size}px "Times New Roman", Times, serif`;
      return measureContext.measureText(text).width;
    }
  } catch (e) {
    measureContext = null;
  }
  return text.length * size * 0.52;
};

/** How many lines `text` takes in a box `width` wide at font `size`, wrapping at spaces. */
export const countLines = (text, size, width) => {
  let lines = 0;
  String(text).split('\n').forEach((paragraph) => {
    const words = paragraph.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines += 1;
      return;
    }
    let line = '';
    words.forEach((word) => {
      const candidate = line ? `${line} ${word}` : word;
      if (textWidth(candidate, size) <= width) {
        line = candidate;
        return;
      }
      if (line) lines += 1;
      // A single word wider than the whole box has to break across lines.
      const wordWidth = textWidth(word, size);
      if (wordWidth > width) {
        lines += Math.ceil(wordWidth / width) - 1;
        line = '';
      } else {
        line = word;
      }
    });
    lines += 1;
  });
  return lines;
};

/**
 * The largest font size (down to `floor`) at which `text` fits a `width` x `height` box.
 * A word is never broken across lines to make it fit: that is only a last resort at the floor,
 * shrinking comes first.
 */
export const fitText = ({ text, width, height, base, floor, lineHeight = 1.15 }) => {
  const words = String(text).split(/\s+/).filter(Boolean);
  for (let size = base; size >= floor; size -= 0.25) {
    const widestWord = words.reduce((max, word) => Math.max(max, textWidth(word, size)), 0);
    if (widestWord <= width) {
      const lines = countLines(text, size, width);
      if (lines * size * lineHeight <= height + 0.01) return { size, lines };
    }
  }
  return { size: floor, lines: countLines(text, floor, width) };
};

const OverlayField = ({ isDownload, d, p, children }) => {
  const pos = isDownload ? d : p;
  if (!pos) return null;
  const { top, left, width, height, fontSize } = pos;
  const base = fontSize || (isDownload ? 14 : 9);

  if (!width) {
    return <div className="overlay" style={{ top, left, fontSize: base }}>{children}</div>;
  }

  const text = (Array.isArray(children) ? children.join('') : children == null ? '' : String(children)).trim();

  if (height) {
    if (!text) return null;
    const { size, lines } = fitText({ text, width, height, base, floor: base * 0.5 });
    const lineHeight = 1.15;
    const offset = Math.max(0, (height - lines * size * lineHeight) / 2);
    return (
      <div
        className="overlay"
        style={{
          top: top + offset,
          left,
          width,
          fontSize: size,
          lineHeight,
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        }}
      >
        {text}
      </div>
    );
  }

  const min = Math.max(base * 0.65, isDownload ? 8 : 6);
  // Times New Roman averages roughly 0.52em per character at this weight --
  // close enough to decide whether shrinking alone gets it to fit, without a
  // canvas/DOM measurement round trip for something purely cosmetic.
  const estimatedWidth = text.length * base * 0.52;
  const size = estimatedWidth > width ? Math.max(min, (width / estimatedWidth) * base) : base;

  return (
    <div
      className="overlay"
      style={{
        top,
        left,
        width,
        fontSize: size,
        lineHeight: 1.15,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
      }}
    >
      {children}
    </div>
  );
};

export default OverlayField;
